import {
	getAppId,
	onConnectivityChange,
	onEventLogAppend,
	readCursor,
	readEventLog,
	writeCursor,
	type ReaderEvent,
} from '@shared/events';

import type { Destination } from './destination';
import { withTabLock } from '@shared/lib/tab-lock';

export interface DispatcherOptions {
	destinations: ReadonlyArray<Destination>;
	/** Settings → Privacy → Analytics, as it is right now. */
	isSharingOn: () => boolean;
	/** Whether a send has a chance of leaving the device. */
	isOnline?: () => boolean;
	/** Runs `drain` in one tab at a time. */
	lock?: (run: () => Promise<void>) => Promise<void>;
}

export interface Dispatcher {
	/**
	 * Brings the destinations in line with the sharing setting and sends what
	 * is waiting. Call it at boot and whenever the setting changes.
	 */
	sync: () => void;
	/** Sends what is waiting. Resolves when this tab has nothing left to do. */
	drain: () => Promise<void>;
	/** Stops listening and stops every destination. */
	close: () => void;
}

const PAGE_SIZE = 100;
const cursorOf = (destination: Destination) => `destination:${destination.name}`;

/**
 * The Analytics Dispatcher: sends the event log to the tenant's
 * destinations, each from its own cursor.
 *
 * - **Offline.** Nothing is sent and no cursor moves; everything goes out,
 *   with its original `event_ts`, when the connection returns.
 * - **A destination added later** has no cursor, so it starts at the oldest
 *   event still kept and receives the retained window. The others are not
 *   sent anything twice.
 * - **Sharing off.** Cursors move past what they find without sending it.
 *   An event recorded while sharing was off is never sent either, whatever
 *   the setting is by the time the dispatcher reaches it (the log stamps
 *   each row). Off is not undone later.
 * - **One tab sends at a time** (`@shared/lib/tab-lock`).
 *
 * Delivery is "attempted": a vendor SDK gives no acknowledgement. One
 * destination failing does not stop the others.
 */
export function createDispatcher({
	destinations,
	isSharingOn,
	isOnline = () => true,
	lock = (run) => withTabLock('cf-events-drain', run),
}: DispatcherOptions): Dispatcher {
	let wasSharing: boolean | undefined;
	let closed = false;
	let running: Promise<void> | undefined;
	let again = false;

	const quietly = (action: () => void) => {
		try {
			action();
		} catch {
			// A vendor SDK throwing must not reach the reader or the next vendor.
		}
	};

	const withAppId = (event: ReaderEvent): ReaderEvent => {
		// Recorded on a first launch that could not fetch the id: it has
		// arrived since, and it never changes for this app.
		const appId = event.app_id ?? getAppId();
		return appId === undefined || event.app_id !== undefined
			? event
			: { ...event, app_id: appId };
	};

	const drainOne = async (destination: Destination): Promise<void> => {
		const name = cursorOf(destination);
		let cursor = (await readCursor(name)) ?? 0;
		for (;;) {
			const rows = await readEventLog(cursor, PAGE_SIZE);
			if (!rows.length) {
				return;
			}
			const before = cursor;
			for (const row of rows) {
				if (closed) {
					break;
				}
				const sharing = isSharingOn();
				if (sharing && !isOnline()) {
					break;
				}
				if (sharing && row.shared) {
					quietly(() => destination.send(withAppId(row.event)));
				}
				cursor = row.seq;
			}
			if (cursor !== before) {
				await writeCursor(name, cursor);
			}
			if (cursor !== rows[rows.length - 1].seq) {
				return;
			}
		}
	};

	const drainAll = async (): Promise<void> => {
		if (closed || !destinations.length) {
			return;
		}
		if (isSharingOn()) {
			// Every time, not once: a vendor SDK that could not be downloaded
			// while offline is asked for again.
			for (const destination of destinations) {
				quietly(() => destination.start?.());
			}
		}
		for (const destination of destinations) {
			try {
				await drainOne(destination);
			} catch {
				// The log could not be read for this one; the next trigger retries.
			}
		}
	};

	const drain = (): Promise<void> => {
		if (running) {
			again = true;
			return running;
		}
		running = (async () => {
			do {
				again = false;
				await lock(drainAll);
			} while (again && !closed);
		})().finally(() => {
			running = undefined;
		});
		return running;
	};

	const offAppend = onEventLogAppend(() => void drain());
	const offConnectivity = onConnectivityChange(() => void drain());

	return {
		sync: () => {
			const sharing = isSharingOn();
			if (wasSharing === true && !sharing) {
				for (const destination of destinations) {
					quietly(() => destination.stop?.());
				}
			}
			// A reader who had opted out before the app started: make sure the
			// trackers the page loaded itself stay silent.
			if (wasSharing === undefined && !sharing) {
				for (const destination of destinations) {
					quietly(() => destination.stop?.());
				}
			}
			wasSharing = sharing;
			void drain();
		},
		drain,
		close: () => {
			closed = true;
			offAppend();
			offConnectivity();
			if (wasSharing) {
				for (const destination of destinations) {
					quietly(() => destination.stop?.());
				}
			}
		},
	};
}
